import { Link, Navigate } from "react-router-dom";
import SEOMeta from "@/components/SEOMeta";
import NavbarNew from "@/components/NavbarNew";
import FooterNew from "@/components/FooterNew";
import ProductDemo from "@/components/automacao/ProductDemo";
import AutomacaoCTAForm from "@/components/automacao/AutomacaoCTAForm";
import {
  AutomacaoFaqSection,
  AutomacaoPricingSection,
  AutomacaoStepsSection,
} from "@/components/automacao/AutomacaoSections";
import { getPillarBySlug, visiblePillars } from "@/data/automacaoServices";
import { cn } from "@/lib/utils";
import { Photo } from "@/components/shared/Photo";

interface ProductPageTemplateProps {
  slug: "crm" | "atendimento" | "erp";
}

const container = "mx-auto max-w-[1180px] px-8 max-[900px]:px-5";

const ProductPageTemplate = ({ slug }: ProductPageTemplateProps) => {
  const pillar = getPillarBySlug(slug);

  if (!pillar) return <Navigate to="/automacao-empresarial" replace />;

  return (
    <div className="min-h-screen bg-background">
      <SEOMeta
        title={`${pillar.tag} (${pillar.systemName}) — Automação Empresarial`}
        description={pillar.fullDescription}
        canonicalUrl={`/automacao-empresarial/${pillar.slug}`}
      />
      <NavbarNew />

      <main>
        <section className={cn(container, "pt-[44px]")}>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/automacao-empresarial"
              className="rounded-full border border-border bg-card px-4 py-[9px] text-[13px] font-bold text-muted-foreground transition-colors hover:text-foreground"
            >
              Visão geral
            </Link>
            {visiblePillars.map((p) => {
              const active = p.slug === pillar.slug;
              return (
                <Link
                  key={p.slug}
                  to={`/automacao-empresarial/${p.slug}`}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "rounded-full border px-4 py-[9px] text-[13px] font-bold transition-colors",
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-muted-foreground hover:text-foreground",
                  )}
                >
                  {p.tag}
                </Link>
              );
            })}
          </div>
        </section>

        <section className={cn(container, "pb-[62px] pt-[44px]")}>
          <div className="grid grid-cols-[1.05fr_.95fr] items-center gap-[52px] max-[900px]:grid-cols-1">
            <div>
              <div className="flex items-center gap-[13px]">
                <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[14px] border border-border bg-white shadow-card">
                  <img
                    src={pillar.logo}
                    alt={pillar.systemName}
                    className="h-[26px] w-[26px] object-contain"
                  />
                </div>
                <span className="inline-flex h-[29px] items-center rounded-full border border-border bg-card px-[13px] text-xs font-bold text-muted-foreground">
                  Sistema-base: {pillar.systemName}
                </span>
              </div>
              <h1 className="mt-5 text-[48px] font-bold leading-[1.08] tracking-[-0.04em] max-[900px]:text-[38px]">
                {pillar.title}
              </h1>
              <div className="mt-[22px] rounded-r-xl border-l-[3px] border-bad bg-bad-soft px-[18px] py-4">
                <span className="text-xs font-bold uppercase tracking-[.05em] text-bad">A dor</span>
                <p className="mt-1.5 text-[15px] leading-[1.6] text-foreground">
                  {pillar.painPoint}
                </p>
              </div>
              <p className="mt-5 max-w-[520px] text-base leading-[1.7] text-muted-foreground">
                {pillar.aiAngle}
              </p>
              <div className="mt-7 flex flex-wrap gap-[11px]">
                <Link
                  to="/contato"
                  className="inline-flex h-[50px] items-center rounded-full bg-primary px-[26px] text-[14.5px] font-bold text-primary-foreground transition-[filter] hover:brightness-95"
                >
                  Agendar diagnóstico gratuito
                </Link>
                <span className="inline-flex items-center text-[13px] text-muted-foreground">
                  Implantação típica: 30 dias
                </span>
              </div>
            </div>
            <ProductDemo
              screenshot={pillar.screenshot}
              screenshotAlt={pillar.screenshotAlt}
              videoId={pillar.videoId}
              videoLabel={pillar.videoLabel}
            />
          </div>
        </section>

        <AutomacaoStepsSection />

        <section className={cn(container, "py-[70px]")}>
          <div className="grid grid-cols-2 items-start gap-11 max-[900px]:grid-cols-1">
            <div>
              <h2 className="mb-[22px] text-[29px] font-bold tracking-[-0.03em]">
                O que está incluso
              </h2>
              <div className="flex flex-col gap-3">
                {pillar.features.map((f) => (
                  <div
                    key={f}
                    className="flex items-start gap-[13px] rounded-[14px] border border-border bg-card px-[18px] py-4"
                  >
                    <span className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent text-[11px] font-bold text-primary">
                      ✓
                    </span>
                    <span className="text-sm leading-[1.55]">{f}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-4">
              {pillar.photo && (
                <div className="aspect-[3/2] overflow-hidden rounded-[18px] border border-border">
                  <Photo id={pillar.photo} sizes="(max-width: 900px) 100vw, 560px" />
                </div>
              )}
              <div className="rounded-[18px] border border-border bg-muted p-7">
                <h3 className="text-base font-bold">Por que open source</h3>
                <p className="mt-3 text-sm leading-[1.7] text-muted-foreground">
                  Você não fica preso a mensalidade por usuário nem a um fornecedor. O sistema é
                  seu, roda na sua infraestrutura, e se um dia quiser levar para outro time, leva.
                </p>
                <div className="mt-5 border-t border-border pt-[18px]">
                  <div className="text-xs font-bold text-muted-foreground">
                    Licença do {pillar.systemName}
                  </div>
                  <p className="mt-[7px] text-[12.5px] leading-[1.6] text-muted-foreground">
                    {pillar.systemLicense}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <AutomacaoPricingSection />

        <AutomacaoFaqSection />

        <section className={cn(container, "pb-[90px]")}>
          <div className="relative isolate grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] items-center gap-9 overflow-hidden rounded-3xl bg-deep px-11 py-[52px] text-deep-foreground max-[900px]:px-6">
            <div className="absolute inset-0 -z-10">
              <Photo id="caixa-loja" decorative sizes="(max-width: 900px) 100vw, 1120px" />
              <div className="absolute inset-0 bg-gradient-to-r from-deep via-deep/[0.92] to-deep/80" />
            </div>
            <div>
              <h2 className="text-[31px] font-bold tracking-[-0.03em]">
                Vamos ver se faz sentido pro seu caso
              </h2>
              <p className="mt-3.5 text-[15.5px] leading-[1.65] text-white/70">
                30 minutos, sem compromisso. Eu já chego com o contexto de {pillar.short} e digo o
                que vale automatizar primeiro.
              </p>
            </div>
            <AutomacaoCTAForm pillarSlug={pillar.slug} pillarTag={pillar.tag} />
          </div>
        </section>
      </main>

      <FooterNew />
    </div>
  );
};

export default ProductPageTemplate;
